import React from "react";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Grid2, IconButton, Switch, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";
import Dropzone from "../../features/FileDropzone/Dropzone.component";
import { FileObject } from "../../features/FileDropzone/typings/FileObject.type";
import { FleetAttachment } from "./types/FleetManagement.types";

type AttachmentsSectionProps = {
  attachments: FleetAttachment[];
  setAttachments: React.Dispatch<React.SetStateAction<FleetAttachment[]>>;
  vehicleId?: number;
  onDeleteExisting?: (vehicleId: number, attachmentId: number) => void | Promise<void>;
};

export function AttachmentsSection({
  attachments,
  setAttachments,
  vehicleId,
  onDeleteExisting
}: Readonly<AttachmentsSectionProps>) {
  const { t } = useTranslation();

  const handleAdd = (newFiles: FileObject[]) => {
    if (!newFiles.length) return;
    const mapped = newFiles.map(file => ({ ...file, isAdminOnly: false }));
    setAttachments(prev => [...prev, ...mapped]);
  };

  const handleRemove = (index: number) => {
    setAttachments(prev => {
      const target = prev[index];

      if (target && typeof target.id === "number" && vehicleId && onDeleteExisting) {
        onDeleteExisting(vehicleId, target.id);
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const toggleAdminOnly = (index: number) => {
    setAttachments(prev =>
      prev.map((attachment, idx) =>
        idx === index ? { ...attachment, isAdminOnly: !attachment.isAdminOnly } : attachment
      )
    );
  };

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12 }}>
        <Typography variant="h6">{t("Attachments")}</Typography>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <Dropzone
          dropzoneDesc={t("Upload attachments (pdf/jpg/png)")}
          acceptedFileTypes={{ "image/*": [], "application/pdf": [] }}
          maxFiles={10}
          onAdd={handleAdd}
          onDelete={(file: FileObject, index: number) => handleRemove(index)}
        />
      </Grid2>
      <Grid2 size={{ xs: 12 }} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {attachments.length ? (
          attachments.map((attachment, index) => (
            <Grid2
              key={`${attachment.file?.name ?? attachment.filename ?? index}-${index}`}
              container
              alignItems={"center"}
              spacing={2}
              wrap="nowrap"
            >
              <Grid2 size={{ xs: 6, sm: 7 }}>
                <Typography>{attachment.file?.name ?? attachment.filename ?? ""}</Typography>
              </Grid2>
              <Grid2 size={{ xs: 5, sm: 5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      data-testid={`attachment-adminOnly-${index}`}
                      checked={!!attachment.isAdminOnly}
                      onChange={() => toggleAdminOnly(index)}
                    />
                  }
                  label={t("Admin only")}
                />
              </Grid2>
              <Grid2 size={{ xs: 1, sm: 2 }}>
                <IconButton
                  size="small"
                  data-testid={`attachment-delete-${index}`}
                  onClick={() => handleRemove(index)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Grid2>
            </Grid2>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("No attachments yet")}
          </Typography>
        )}
      </Grid2>
    </Grid2>
  );
}
